import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { ReportWidgetChartTooltip } from './ReportWidgetTooltipContent';

import type {
    ReportWidgetChartPoint,
    ReportWidgetItem,
} from '../../model/types';

interface ReportWidgetBarChartProps {
    data: ReportWidgetChartPoint[];
    showLegend: boolean;
    tooltipFormatter: (value: number) => string;
    items: ReportWidgetItem[];
    total: number;
    onItemClick?: (item: ReportWidgetItem) => void;
    width?: number;
    height?: number;
    activeItemId?: string;
    disableAnimation?: boolean;
    onItemHover?: (item: ReportWidgetItem | null) => void;
}

export const ReportWidgetBarChart = ({
    data,
    showLegend,
    tooltipFormatter,
    items,
    total,
    onItemClick,
    width,
    height,
    activeItemId,
    disableAnimation = false,
    onItemHover,
}: ReportWidgetBarChartProps) => (
    <BarChart
        data={data}
        width={width}
        height={height}
        margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
    >
        <CartesianGrid
            stroke="#E5E7EB"
            strokeDasharray="3 3"
            vertical={false}
        />
        <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: '#000000' }}
            tickLine={false}
            interval={0}
        />
        <YAxis
            tick={{ fontSize: 12, fill: '#000000' }}
            tickLine={false}
            tickFormatter={(v) => (typeof v === 'number' ? `$${v}` : String(v))}
        />
        <Tooltip
            cursor={{ fill: 'transparent' }}
            content={
                <ReportWidgetChartTooltip
                    tooltipFormatter={tooltipFormatter}
                    total={total}
                    items={items}
                />
            }
        />
        {showLegend && (
            <Legend
                verticalAlign="top"
                align="left"
                iconType="circle"
                wrapperStyle={{
                    paddingBottom: '18px',
                    fontSize: '12px',
                    lineHeight: '16px',
                }}
            />
        )}
        <Bar
            dataKey="value"
            fill="#5ca1ff"
            radius={[4, 4, 0, 0]}
            isAnimationActive={!disableAnimation}
            cursor={onItemClick ? 'pointer' : 'default'}
            maxBarSize={150}
        >
            {data.map((entry, index) => {
                const isActive = entry.id === activeItemId;
                const hasActive = Boolean(activeItemId);
                const item = items[index];
                const baseOpacity = 0.9;
                const activeOpacity = 1;
                const inactiveOpacity = 0.75;

                let fillOpacity = baseOpacity;
                if (hasActive) {
                    fillOpacity = isActive ? activeOpacity : inactiveOpacity;
                }

                return (
                    <Cell
                        key={entry.id}
                        fill={entry.color}
                        fillOpacity={fillOpacity}
                        style={{
                            transition: 'fill-opacity 120ms ease-out',
                        }}
                        onClick={() => onItemClick && item && onItemClick(item)}
                        onMouseEnter={() => {
                            if (onItemHover && item) {
                                onItemHover(item);
                            }
                        }}
                        onMouseLeave={() => {
                            if (onItemHover) {
                                onItemHover(null);
                            }
                        }}
                    />
                );
            })}
        </Bar>
    </BarChart>
);
